import { KatSpinner } from "@amzn/katal-react";
import React, { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import type { NestUseCase } from "src/api/interfaces/nest";
import { NestTable } from "./components/NestTable";
import { NestUploadCard } from "./components/Upload/NestUploadCard";
import { NestUploadProgressFooter } from "./components/Upload/NestUploadProgressFooter";
import { NestUploadResult } from "./components/Upload/NestUploadResult";
import "./index.css";
import AICLayout from "src/components/AICLayout/AICLayout";
import NestHistory from "./components/History";
import { NestHistoryDetail } from "./components/History/NestHistoryDetail";
import { useNestContent } from "./hooks/useNestContent";
import { useDocumentTypePreference } from "./hooks/useDocumentTypePreference";
import { NestPartnerAlert } from "./components/NestPartnerAlert";
import { NestExpectationsPanel } from "./components/NestExpectationsPanel";
import { useCountrySelection } from "src/components/Contexts/CountrySelectionContext";
import { useAlertContext } from "src/components/Alert/AlertContext";
import { alertHandler } from "src/components/Alert/handlers";

export default function Nest() {
  return (
    <div>
      <AICLayout alertClassName="nest-alert-box" isNest>
        <NestContent />
      </AICLayout>
    </div>
  );
}

function NestContent() {
  const { t } = useTranslation();
  const { getDocumentType } = useDocumentTypePreference();
  const { selectedCountry } = useCountrySelection();
  const { alerts, setAlerts } = useAlertContext();
  const {
    useCases,
    isLoading,
    selectedUploadUseCase,
    uploadFiles,
    uploadUseCaseId,
    uploadDocumentTypeId,
    completedUploads,
    useCaseHistory,
    selectedUploadTest,
    handleUpload,
    onNewTest,
    handleFilesSelected,
    handleUploadComplete,
    handleRetry,
    handleSuccessClick,
    handleStatusClick,
    handleHistory,
    resetState,
  } = useNestContent();

  useEffect(() => {
    if (selectedCountry) {
      resetState();
    }
  }, [selectedCountry, resetState]);

  const onStatusClick = useCallback(
    async (useCase: NestUseCase) => {
      try {
        await handleStatusClick(useCase);
      } catch {
        alertHandler.createNewAlertEntry({
          fade: true,
          text: t("nest_status_click_error"),
          isSuccess: false,
          alerts,
          setAlerts,
        });
      }
    },
    [handleStatusClick, alerts, setAlerts, t]
  );

  return (
    <div className="nest-container">
      <NestPartnerAlert />
      <div className="nest-content">
        <div>
          <h1>{t("aic_nest_home_page_title")}</h1>
          <p>{t("aic_nest_home_page_description")}</p>
          {isLoading ? (
            <KatSpinner />
          ) : (
            <NestTable
              data={useCases}
              onUpload={handleUpload}
              onHistory={handleHistory}
              onStatusClick={onStatusClick}
            />
          )}
          {selectedUploadUseCase && (
            <NestUploadCard
              useCase={selectedUploadUseCase}
              documentTypeName={getDocumentType(selectedUploadUseCase).name}
              onFilesSelected={(files) =>
                handleFilesSelected(files, selectedUploadUseCase.id)
              }
            />
          )}
          {completedUploads.length > 0 && (
            <NestUploadResult
              uploads={completedUploads}
              onClose={() => resetState({ useCaseHistory, selectedUploadTest })}
              onRetry={handleRetry}
              onSuccessClick={handleSuccessClick}
            />
          )}
        </div>
        <div
          className={`overflow-x-hidden min-w-0 p-2${useCaseHistory || selectedUploadTest ? " nest-history-panel" : ""}`}
        >
          {selectedUploadTest ? (
            <div className="nest-history">
              <NestHistoryDetail
                test={selectedUploadTest}
                onBack={() => resetState()}
                onNewTest={onNewTest}
              />
            </div>
          ) : useCaseHistory ? (
            <NestHistory
              useCase={useCaseHistory}
              onNewTest={onNewTest}
              onClose={() => resetState()}
            />
          ) : selectedUploadUseCase ? (
            <NestExpectationsPanel
              useCase={selectedUploadUseCase}
              documentTypeName={getDocumentType(selectedUploadUseCase).name}
            />
          ) : null}
        </div>
      </div>
      {uploadFiles.length > 0 && (
        <NestUploadProgressFooter
          files={uploadFiles}
          useCaseId={uploadUseCaseId}
          documentTypeId={uploadDocumentTypeId}
          onComplete={handleUploadComplete}
        />
      )}
    </div>
  );
}
